/**
 *  Mixin to restify a given model.
 * 
 *  Returns the set of methods (POST, GET, PUT, PATCH, DELETE, COLLECTION) 
 *  applied to the given model and accesible using a simple Api:
 *
 *    import { restify } from '@/api'
 *  
 *    const somesApi = restify(SomeModel, { 
 *        endpoint: (uid) => { return (uid ? '/somes'+uid : '/somes' ) }
 *    })
 * 
 *    try {
 *      let rsvp = await somesApi.get(uid)   
 *      console.log("Done somesApi.get()=", response)
 *    }
 *    catch (err) {
 *      console.log("Error in somesApi.get()=", err)
 *    }
 * 
 *  MAZ - 13-abr-2020
 */

import rest from "./rest"

export function restify(model, options) {
  /**
   * Mixin para agregar comportamiento Rest 
   * sobre las diferentes entidades del modelo.
   */
  var _this = {} ;

  // assert restify() required args
  if (!model || !options)
    throw (new rest.Exception(400, 
      '400: Bad request: restify() requires "model" and "options" params'))

  // asert the required "endpoint" option
  if (!options.endpoint) 
    throw (new rest.Exception(400, 
      '400: Bad request: restify() requires "options.endpoint" function'))
  var _endpoint = options.endpoint 

  // assert optional "methods" option
  // if set allow only given methods
  // if not set, allow all of them
  var _methods = options.methods || "collection,post,get,put,patch,delete,upload"
  _methods = _methods.toLowerCase()

  function assertAllowed(method) {
    // Assert that this method is in the list of allowed methods.
    if (_methods.indexOf(method) < 0) 
      throw (new rest.Exception(405, 
        '405: Method "'+method+'" Not Allowed')) 
  }

  _this.collection = async function(filters) {
    assertAllowed('collection')
    const rsvp = await rest.get(_endpoint(), filters)
    // returns { count: n, data: [ {...}, ] }
    const arr = rsvp.data || rsvp || [];
    for (var j=0; j < arr.length; j++) {
      let t = await model.import(arr[j]);
      arr[j] = t ;
    }
    return arr
  }

  _this.post = async function(data, params) {
    assertAllowed('post')
    const rsvp = await rest.post(_endpoint(), model.export(data), params)
    return rsvp
  }

  _this.get = async function(uid, params) {
    assertAllowed('get')
    const rsvp = await rest.get(_endpoint(uid), params)
    return model.import(rsvp)
  }

  _this.put = async function(uid, data, params) {
    assertAllowed('put')
    const rsvp = await rest.put(_endpoint(uid), model.export(data), params)
    return rsvp
  }

  _this.patch = async function(uid, data, params) {
    assertAllowed('patch')
    const rsvp = await rest.patch(_endpoint(uid), model.export(data), params)
    return rsvp
  }

  _this.delete = async function(uid, params) {
    assertAllowed('delete')
    const rsvp = await rest.delete(_endpoint(uid), params)
    return rsvp
  }

  _this.upload = async function(uid, files, params) {
    assertAllowed('upload')
    const rsvp = await rest.upload(_endpoint(uid)+'/files', files, params)
    return rsvp
  }

  return _this
}
